import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

// masters (RTK Query)
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";

// push to store
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setVendorList } from "../slices/vendorSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";
import { setStoneList } from "../slices/stoneSlice";

import {
  useGetUnlabelledByIdQuery,
  useUpdateUnlabelledStockMutation,
} from "../toolkitServices/unlabelledApi";

import UnlabelledStockForm from "../components/UnlabelledStockForm";
import { useGetCountersQuery } from "../toolkitServices/counterApi";
import { setCounterList } from "../slices/counterSlice";

const toOpt = (id, label) =>
  id ? { value: String(id), label: String(label ?? id) } : null;

const mapApiDocToForm = (doc = {}) => ({
  vendor: toOpt(doc.vendorId, doc.vendorName),
  branch: toOpt(doc.branchId, doc.branchName),
  counter: toOpt(doc.counterId, doc.counterName),
  stock: {
    category: toOpt(doc.categoryId, doc.categoryName),
    product: toOpt(doc.productId, doc.productName),
    design: toOpt(doc.designId, doc.designName),
    purity: toOpt(doc.purityId, doc.purityName),

    totalWt: doc.totalWt ?? "",
    packingWt: doc.packingWt ?? "",
    grossWt: doc.grossWt ?? "",
    stoneWt: doc.stoneWt ?? "",
    diamondWt: doc.diamondWt ?? "",
    netWt: doc.netWt ?? "",
    qty: doc.qty ?? "",
    finePercentage: doc.finePercentage ?? "",
    wastagePercentage: doc.wastagePercentage ?? "",
    fineWastageWt: doc.fineWastageWt ?? "",
    description: doc.description ?? "",
  },
});

const EditUnlabelledStock = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // fetch masters
  const { data: vendors } = useGetAllVendorsQuery({ page: 1, limit: 100, search: "" });
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();
  const { data: stones } = useGetAllStonesQuery();
  const { data: counters } = useGetCountersQuery()

  // push masters into store
  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (designs?.data?.length) dispatch(setDesignList(designs.data));
    if (purities?.data?.length) dispatch(setPurityList(purities.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
    if (stones?.data?.length) dispatch(setStoneList(stones.data));
    if (counters?.data?.length) dispatch(setCounterList(counters.data));
  }, [dispatch, categories, products, vendors, designs, purities, branches, stones,counters]);

  // fetch the document
  const { data: detailRes, isFetching, isError } = useGetUnlabelledByIdQuery(id);

  const existingData = useMemo(() => {
    const d = detailRes?.data ?? detailRes;
    return d ? mapApiDocToForm(d) : null;
  }, [detailRes]);

  const [updateDoc, { isLoading: updating }] = useUpdateUnlabelledStockMutation();

  const onUpdateSubmit = async (form) => {
    // light client-side checks (same as create)
    if (!form?.vendor?.value) return toast.error("Vendor is required");
    if (!form?.branch?.value) return toast.error("Branch is required");
    if (!form?.stock?.category?.value) return toast.error("Category is required");
    if (!form?.stock?.product?.value) return toast.error("Product is required");
    if (!form?.stock?.design?.value) return toast.error("Design is required");
    if (!form?.stock?.purity?.value) return toast.error("Purity is required");

    try {
      await updateDoc({ id, ...form }).unwrap();
      toast.success("Unlabelled stock updated");
      navigate(-1); // back to list
    } catch (e) {
      const msg = e?.data?.msg || e?.data?.message || e?.error || "Update failed";
      toast.error(msg);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="max-w-8xl mx-auto bg-white shadow-lg rounded-xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#8a79d7] to-[#6b59c6] text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/15 hover:bg-white/25"
            title="Back"
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-lg font-semibold">Edit Unlabelled Stock</h1>
        </div>
        <div className="text-xs opacity-90">Ctrl + Backspace to go back</div>
      </div>

      <div className="px-2 pt-2 pb-4">
        {isFetching ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : isError ? (
          <div className="p-6 text-sm text-rose-600">Failed to load item.</div>
        ) : (
          <UnlabelledStockForm
            existingData={existingData}
            submitLabel={updating ? "Updating…" : "Update"}
            onSubmitOverride={onUpdateSubmit}
          />
        )}
      </div>
    </motion.div>
  );
};

export default EditUnlabelledStock;
